import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Mail, Clock, MapPin, Package, Calendar } from "lucide-react";
import SEO from "../components/SEO";

const hours = [
  { day: "Monday – Friday", time: "9:00 AM – 5:00 PM" },
  { day: "Saturday", time: "10:00 AM – 2:00 PM" },
  { day: "Sunday", time: "Closed" },
];

export default function ContactPage() {
  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <SEO
        title="Contact Us | Cotton Candy Girl"
        description="Get in touch with Cotton Candy Girl for wholesale cotton candy orders and live event spinning in Las Vegas and Boise. Email shea@cottoncandygirl.co."
        keywords="contact cotton candy girl, wholesale cotton candy contact, cotton candy events Las Vegas, cotton candy Boise"
        canonical="/contact"
      />

      {/* Hero */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-pink-50 to-[#FFFCF9]">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 bg-[#FF9EAA]/20 text-[#E07A8A] text-sm font-bold px-4 py-2 rounded-full mb-6">
            <Mail size={16} />
            LET&apos;S TALK
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A4A4A] mb-6">
            Contact <span className="text-[#FF9EAA]">Us</span>
          </h1>
          <p className="text-lg md:text-xl text-[#888888] leading-relaxed">
            Questions about wholesale orders, UV-activated cotton candy, or a live spinning request for your venue? We&apos;d love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Details */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-3xl p-8 border border-pink-100 text-center" data-testid="contact-email-card">
            <div className="w-16 h-16 rounded-2xl bg-[#FF9EAA] flex items-center justify-center mx-auto mb-6">
              <Mail className="text-white" size={30} />
            </div>
            <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">Email</h3>
            <a
              href="mailto:shea@cottoncandygirl.co"
              className="text-[#E07A8A] font-semibold hover:underline break-all"
              data-testid="contact-email-link"
            >
              shea@cottoncandygirl.co
            </a>
            <p className="text-[#888888] text-sm mt-3">We typically reply within 1 business day.</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border border-purple-100 text-center" data-testid="contact-hours-card">
            <div className="w-16 h-16 rounded-2xl bg-purple-500 flex items-center justify-center mx-auto mb-6">
              <Clock className="text-white" size={30} />
            </div>
            <h3 className="text-xl font-bold text-[#4A4A4A] mb-4">Business Hours</h3>
            <ul className="space-y-2 text-sm">
              {hours.map((h) => (
                <li key={h.day} className="flex justify-between gap-4 text-[#4A4A4A]">
                  <span className="font-medium">{h.day}</span>
                  <span className="text-[#888888]">{h.time}</span>
                </li>
              ))}
            </ul>
            <p className="text-[#888888] text-xs mt-4">Mountain / Pacific Time</p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 border border-blue-100 text-center" data-testid="contact-locations-card">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mx-auto mb-6">
              <MapPin className="text-white" size={30} />
            </div>
            <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">Service Areas</h3>
            <p className="text-[#4A4A4A] font-medium">Las Vegas, Nevada</p>
            <p className="text-[#4A4A4A] font-medium">Boise, Idaho</p>
            <p className="text-[#888888] text-sm mt-3">Wholesale shipping available nationwide.</p>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#FFFCF9]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-base md:text-lg font-semibold text-[#888888] uppercase tracking-wide mb-8">
            Looking for something specific?
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/wholesale">
              <Button
                className="bg-[#FF9EAA] hover:bg-[#FF8FAB] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all"
                data-testid="contact-wholesale-btn"
              >
                <Package className="mr-2" size={20} />
                Wholesale Inquiry
              </Button>
            </Link>
            <Link to="/events">
              <Button
                variant="outline"
                className="border-2 border-[#FF9EAA] text-[#E07A8A] hover:bg-pink-50 rounded-full px-8 py-6 text-lg font-semibold transition-all"
                data-testid="contact-events-btn"
              >
                <Calendar className="mr-2" size={20} />
                Request Live Spinning
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
